import { useState } from 'react';

import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { AxiosError } from 'axios';

interface Props {
  open: boolean;
  preview: string | null;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}

/**
 * 메모 삭제 확인 다이얼로그.
 * Discharge 등 다른 destructive action과 같은 MUI 패턴으로 통일.
 */
export function MemoDeleteDialog({ open, preview, onClose, onConfirm }: Props) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const handleConfirm = async () => {
    setBusy(true);
    setError(null);
    try {
      await onConfirm();
      onClose();
    } catch (e: unknown) {
      if (e instanceof AxiosError) {
        const msg = (e.response?.data as { message?: string | string[] } | undefined)?.message;
        setError(Array.isArray(msg) ? msg.join(', ') : (msg ?? '삭제 처리 중 오류가 발생했습니다'));
      } else {
        setError('알 수 없는 오류가 발생했습니다');
      }
    } finally {
      setBusy(false);
    }
  };

  const handleClose = () => {
    if (busy) return;
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
      <DialogTitle>메모 삭제</DialogTitle>
      <DialogContent>
        <DialogContentText>이 메모를 삭제하시겠습니까?</DialogContentText>
        {preview && (
          <DialogContentText
            sx={{
              mt: 1.5,
              p: 1.5,
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              bgcolor: 'background.default',
              fontStyle: 'italic',
              whiteSpace: 'pre-wrap',
              fontSize: '0.875rem',
              maxHeight: 120,
              overflow: 'auto',
            }}
          >
            {preview}
          </DialogContentText>
        )}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={busy}>
          취소
        </Button>
        <Button color="error" variant="contained" onClick={handleConfirm} disabled={busy}>
          {busy ? '삭제 중…' : '삭제'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
